import { Input } from "@jammable/ui-components/Forms";
import { Spinner } from "@jammable/ui-components/Loader";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { Button, toast } from "@jammable/ui-core";
import {
  FaFileAudio,
  FaInstagram,
  FaInstagramSquare,
  FaMicrophone,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import SoundWave from "@/svgs/SoundWave";
import ConversionSongLibrary from "@/components/conversion/ConversionSongLibrary";
import React, { useCallback, useState, useEffect } from "react";
import { useFormik } from "formik";
import { useQuery } from "react-query";
import { useAuth } from "@/context/AuthProvider";
import ConversionRecentlyUsed from "@/components/conversion/ConversionRecentlyUsed";

import dayjs from "dayjs";
import { formatSeconds } from "@jammable/ui-components/AudioPlayer/utils";
import { checkDuration } from "@/utils/tools";

import { AiFillInstagram } from "react-icons/ai";
import InstaIcon from "@/svgs/InstaIcon";
import TikTokIcon from "@/svgs/TikTokIcon";
const UploadSong = ({
  model,
  nextStage,
  setFinalUrl,
  setTitle,
  setLibrarySong,
  setProgress,
  setUpgradeModalOpen,
  setIsOpen,
  setMode,
  uploadedFileId,
  setUploadedFileId,
  selectedButton,
  setSelectedButton,
  setMetadata,
  downloadReliability,
  setAudioEffects,
}) => {
  const [loading, setLoading] = useState(false);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [noCredits, setNoCredits] = useState(false);

  const { user } = useAuth();

  // Set default audio effects
  useEffect(() => {
    // Set default audio effects based on the example provided
    const defaultAudioEffects = [
      {
        HighpassFilter: {
          cutoff_frequency_hz: 175,
        },
      },
      {
        LowpassFilter: {
          cutoff_frequency_hz: 14000,
        },
      },
      {
        PeakFilter: {
          cutoff_frequency_hz: 8000,
          gain_db: 4.0,
          q: 0.7,
        },
      },
      {
        NoiseGate: {
          threshold_db: -50,
          ratio: 10,
          attack_ms: 1.0,
          release_ms: 100.0,
        },
      },
      {
        Compressor: {
          threshold_db: -20,
          ratio: 10,
          attack_ms: 1.0,
          release_ms: 100.0,
        },
      },
      {
        Reverb: {
          room_size: 0.75,
          damping: 1.0,
          wet_level: 0.03,
          dry_level: 0.4,
          width: 0.1,
          freeze_mode: 0.0,
        },
      },
      {
        Chorus: {
          rate_hz: 0.6,
          depth: 0.1,
          centre_delay_ms: 4.0,
          feedback: 0.0,
          mix: 0.2,
        },
      },
      {
        Delay: {
          delay_seconds: 0.1,
          feedback: 0.0,
          mix: 0.1,
        },
      },
      {
        Gain: {
          gain_db: 14,
        },
      },
    ];

    setAudioEffects(defaultAudioEffects);
  }, [setAudioEffects]);

  const handleUpload = async (key, file, url, token, absoluteUrl) => {
    const options = {
      headers: {
        "Content-Type": "audio/wav",
        // "x-amz-storage-class": "INTELLIGENT_TIERING",
      },
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        //progress goes to 100 before request done so need to cap it at 99 before http put is fully done
        const progress = Math.round((loaded / total) * 99);
        setProgress(progress);
      },
    };
    axios.put(url, file, options).then(() => {
      setUploadedFileId(null);
      setProgress(100);
    });
    const s3Url = absoluteUrl;
    setFinalUrl(s3Url);
    nextStage();
  };

  const getPresignedUrl = async (token) => {
    const response = await axios.get("/api/s3/modelS3PresignedUrl", {
      params: { model: model?.urlParam },
      headers: { Authorization: token },
    });
    return {
      url: response.data.url,
      key: response.data.key,
      absoluteUrl: response.data.absoluteUrl,
    };
  };

  const handleFileUpload = useCallback(
    async (acceptedFiles, fileRejections) => {
      if (acceptedFiles?.length > 0) {
        setLoading(true);
        const file = acceptedFiles[0];
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");

        setTitle(nameWithoutExtension);
        if (file.size > 50 * 1024 * 1024) {
          toast.error("File size exceeds 50MB! Please upload a smaller file.");
          setLoading(false);
          return;
        }
        const duration = await checkDuration(file);
        if (duration && duration > 6 * 60) {
          toast.error(
            "Audio duration exceeds 6 minutes! Please upload a shorter file.",
          );
          setLoading(false);
          return;
        }

        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();

        try {
          const { key, url, absoluteUrl } = await getPresignedUrl(token);
          await handleUpload(key, file, url, token, absoluteUrl);
        } catch (err) {
          handleError(err);
        } finally {
          setLoading(false);
        }
      } else {
        toast.error("Please upload a valid audio file");
      }
    },
    [],
  );

  const handleError = (err) => {
    setLoading(false);
    setYoutubeLoading(false);
    if (err?.response?.data?.message === "You are out of credits") {
      setNoCredits(true);
      setUpgradeModalOpen({ isOpen: true, dailyLimit: true });

      setIsOpen(false);
      // setUpgradeModalOpen({ isOpen: true, dailyLimit: true });
    } else {
      toast.error(err?.response?.data?.message);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
    maxFiles: 1,
    accept: {
      "audio/*": [".wav", ".mp3", ".m4a"],
    },
  });

  const buttonSelectedStyle = `tw-bg-purple-700 tw-text-white`;
  const buttonCommonStyle = `tw-p-2 tw-px-2.5  tw-rounded-full tw-cursor-pointer tw-text-gray-500 tw-font-black tw-transition-all`;

  const validationSchema = yup.object().shape({
    url: yup
      .string()
      .trim()
      .url("Please enter a valid social link")
      .required("Social link is required"),
  });

  const formik = useFormik({
    initialValues: {
      url: "",
    },
    validationSchema,
    onSubmit: async (values, error) => {
      setYoutubeLoading(true);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      try {
        await axios
          .post(
            `/api/s3/youtubeDownload`,
            { url: values.url.trim(), model: model?.urlParam },
            { headers: { Authorization: token } },
          )
          .then((res) => {
            setMode("youtube");
            setProgress(100);
            setTitle(res?.data?.title);
            setFinalUrl(res?.data?.s3URL);
            setMetadata(res?.data?.metadata);
            setUploadedFileId(null);
            nextStage();
          });
      } catch (err) {
        handleError(err);
      } finally {
        setYoutubeLoading(false);
      }
    },
  });

  const handleFormSubmit = (event) => {
    event.preventDefault();

    // Perform additional actions here
    console.log(formik.errors.url);
    if (formik.errors.url) {
      toast.error(formik.errors.url);
      return;
    }

    // Then call formik's submit function
    formik.handleSubmit();
  };

  const getDownloadReliability = () => {
    const successRate =
      downloadReliability?.metrics?.hourly?.total < 20
        ? downloadReliability?.metrics?.overall.reliability
        : downloadReliability?.metrics?.hourly.reliability;
    if (successRate > 0.9) {
      return "High";
    } else if (successRate > 0.3) {
      return "Medium";
    } else {
      return "Low";
    }
  };

  return (
    <section className="tw-p-0 tw-py-4  md:tw-py-0 tw-w-full   tw-flex tw-flex-col tw-items-center tw-outline-none tw-truncate ">
      <>
        {/* <div className="tw-font-bold tw-text-xl tw-mb-4 tw-text-center tw-flex tw-flex-col tw-items-center tw-gap-4">
            Provide an input for {model?.artistname}...
          </div> */}
        <div className="tw-flex tw-w-max tw-bg-gray-100 tw-p-1 tw-rounded-full tw-font-bold tw-gap-1 tw-items-center tw-mb-6">
          <div
            onClick={() => {
              setSelectedButton("upload");
            }}
            className={`${selectedButton === "upload" ? buttonSelectedStyle : ""} ${buttonCommonStyle} `}
          >
            Upload Song
          </div>
          <div
            onClick={() => {
              setSelectedButton("library");
            }}
            className={`${selectedButton === "library" ? buttonSelectedStyle : ""} ${buttonCommonStyle} tw-flex tw-gap-1`}
          >
            Library
          </div>

          <div
            onClick={() => {
              setSelectedButton("recent");
            }}
            className={`${selectedButton === "recent" ? buttonSelectedStyle : ""} ${buttonCommonStyle} tw-flex tw-gap-1`}
          >
            Recent
          </div>
        </div>

        {selectedButton === "upload" ? (
          <section
            {...getRootProps({
              onClick: (event) => {},
            })}
            className="tw-w-full tw-px-6 tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-border-dashed tw-border-2 tw-border-gray-300 tw-rounded-3xl tw-outline-none"
          >
            {loading ? (
              <div className="tw-p-10  tw-px-32">
                <Spinner className="tw-border-3" />
              </div>
            ) : (
              <>
                <form
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onSubmit={handleFormSubmit}
                  className="tw-flex tw-flex-col tw-gap-3 tw-w-full tw-max-w-xl"
                >
                  <input
                    className="tw-w-full tw-bg-red-900"
                    {...getInputProps()}
                  />
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {downloadReliability && (
                      <div className="tw-font-black tw-text-[10px] tw-tracking-widest tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                        <div
                          className={`tw-size-1.5  tw-rounded-full ${getDownloadReliability() === "High" ? "tw-bg-green-500" : getDownloadReliability() === "Medium" ? "tw-bg-yellow-500" : "tw-bg-red-500"}`}
                        />
                        <span>STATUS</span>
                      </div>
                    )}
                    <Input
                      size="xl"
                      className="tw-w-full md:tw-min-w-96 tw-rounded-2xl tw-text-black/50 tw-h-14 tw-font-bold tw-text-base"
                      name="url"
                      value={formik.values.url}
                      onChange={formik.handleChange}
                      disabled={youtubeLoading}
                      onBlur={formik.handleBlur}
                      placeholder={"Paste a social link..."}
                      icon={
                        <div className="tw-flex tw-items-center tw-justify-center tw-px-4 ">
                          <InstaIcon
                            className="tw-absolute  tw-left-2 tw--rotate-[22deg]  tw-top-4"
                            size={24}
                          />
                          <FaYoutube
                            className="tw-absolute tw-z-10"
                            color="red"
                            size={26}
                          />
                          <TikTokIcon
                            className="tw-absolute tw-rotate-[22deg] tw-right-1 tw-top-4"
                            size={22}
                          />
                        </div>
                      }
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={youtubeLoading}
                    className="tw-rounded-full"
                    onClick={handleFormSubmit}
                  >
                    {youtubeLoading ? (
                      <Spinner
                        size={4}
                        className="tw-border-white tw-border-3"
                      />
                    ) : (
                      <SoundWave
                        size={30}
                        color="white"
                        opacity={1}
                        style={{ marginRight: 5 }}
                      />
                    )}

                    <div>{youtubeLoading ? "Checking Link" : "Continue"}</div>
                  </Button>
                </form>
                <div className="tw-font-bold tw-text-md tw-text-purple-700 tw-mt-4">
                  Or drop an audio file...
                </div>
              </>
            )}
          </section>
        ) : selectedButton === "library" ? (
          <ConversionSongLibrary
            setFinalUrl={setFinalUrl}
            setProgress={setProgress}
            setLibrarySong={setLibrarySong}
            nextStage={nextStage}
          />
        ) : (
          // null

          <ConversionRecentlyUsed
            setFinalUrl={setFinalUrl}
            setProgress={setProgress}
            setLibrarySong={setLibrarySong}
            nextStage={nextStage}
            setUploadedFileId={setUploadedFileId}
            setSelectedButton={setSelectedButton}
            setTitle={setTitle}
          />
        )}
      </>
    </section>
  );
};

export default UploadSong;
